import { httpRequest } from '@/services/httpClient'

// ─── Connector Types ───

export type ConnectorType = 'api' | 'database' | 'file' | 'webhook'

export type ConnectorItem = {
  id: number
  tenantId: number
  name: string
  description: string | null
  type: ConnectorType
  config: string
  isActive: boolean
  lastTestedAt: string | null
  lastTestedStatus: string | null
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string | null
}

export type ConnectorListResponse = {
  items: ConnectorItem[]
  totalCount: number
  page: number
  pageSize: number
}

export type ConnectorListParams = {
  search?: string
  type?: ConnectorType
  isActive?: boolean
  page?: number
  pageSize?: number
  sortBy?: 'name' | 'type' | 'lasttestedat'
  sortDir?: 'asc' | 'desc'
}

export type ConnectorPayload = {
  name: string
  description?: string
  type: ConnectorType
  config: string
  isActive?: boolean
}

// ─── Mapping Types ───

export type FieldMapping = {
  sourceField: string
  targetField: string
  transformer?: string
  isRequired?: boolean
  defaultValue?: string
}

export type ValidationRule = {
  field: string
  rule: 'required' | 'email' | 'date' | 'number' | 'minLength' | 'maxLength' | 'regex'
  params?: Record<string, unknown>
  errorMessage?: string
}

export type MappingItem = {
  id: number
  tenantId: number
  name: string
  description: string | null
  sourceSystem: string
  targetEntity: string
  connectorId: number | null
  connectorName: string | null
  mappings: string
  validationRules: string | null
  isActive: boolean
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string | null
}

export type MappingListResponse = {
  items: MappingItem[]
  totalCount: number
  page: number
  pageSize: number
}

export type MappingListParams = {
  search?: string
  targetEntity?: string
  connectorId?: number
  isActive?: boolean
  page?: number
  pageSize?: number
  sortBy?: 'name' | 'sourcesystem' | 'targetentity'
  sortDir?: 'asc' | 'desc'
}

export type MappingPayload = {
  name: string
  description?: string
  sourceSystem: string
  targetEntity: string
  connectorId?: number | null
  mappings: string
  validationRules?: string
  isActive?: boolean
}

// ─── Session Types ───

export type SessionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

export type SessionItem = {
  id: number
  tenantId: number
  mappingId: number
  mappingName: string
  connectorType: string
  targetEntity: string
  status: SessionStatus
  totalRows: number
  successCount: number
  errorCount: number
  skippedCount: number
  errors?: string
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  executedBy: string | null
  createdAt: string
}

export type SessionListResponse = {
  items: SessionItem[]
  totalCount: number
  page: number
  pageSize: number
}

export type LogItem = {
  id: number
  sessionId: number
  entityType: string
  entityId: string | null
  action: string
  oldData: string | null
  newData: string | null
  errorMessage: string | null
  rowNumber: number | null
  createdAt: string
}

// ─── Constants ───

export const CONNECTOR_TYPES: { value: ConnectorType; labelKey: string; icon: string; descKey: string }[] = [
  { value: 'api', labelKey: 'integrations.connectorTypes.api', icon: 'cloud', descKey: 'integrations.connectorTypes.apiDesc' },
  { value: 'database', labelKey: 'integrations.connectorTypes.database', icon: 'database', descKey: 'integrations.connectorTypes.databaseDesc' },
  { value: 'file', labelKey: 'integrations.connectorTypes.file', icon: 'file-text', descKey: 'integrations.connectorTypes.fileDesc' },
  { value: 'webhook', labelKey: 'integrations.connectorTypes.webhook', icon: 'zap', descKey: 'integrations.connectorTypes.webhookDesc' },
]

export const TARGET_ENTITIES = [
  'Employee', 'Department', 'Position', 'Location', 'Organization',
] as const

// ─── API Client ───

export const connectorApi = {
  list: (params: ConnectorListParams) =>
    httpRequest<ConnectorListResponse>('/v1/integration-connectors', {
      method: 'GET',
      query: {
        search: params.search?.trim() || undefined,
        type: params.type || undefined,
        isActive: params.isActive,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      },
    }),

  getById: (id: number) =>
    httpRequest<ConnectorItem>(`/v1/integration-connectors/${id}`, { method: 'GET' }),

  create: (payload: ConnectorPayload) =>
    httpRequest<{ id: number; name: string; type: string }>('/v1/integration-connectors', {
      method: 'POST',
      body: payload,
    }),

  update: (id: number, payload: ConnectorPayload) =>
    httpRequest<ConnectorItem>(`/v1/integration-connectors/${id}`, {
      method: 'PUT',
      body: payload,
    }),

  delete: (id: number) =>
    httpRequest<undefined>(`/v1/integration-connectors/${id}`, { method: 'DELETE' }),

  test: (id: number) =>
    httpRequest<{ success: boolean; status: string; testedAt: string }>(
      `/v1/integration-connectors/${id}/test`,
      { method: 'POST' },
    ),
}

export const mappingApi = {
  list: (params: MappingListParams) =>
    httpRequest<MappingListResponse>('/v1/data-mappings', {
      method: 'GET',
      query: {
        search: params.search?.trim() || undefined,
        targetEntity: params.targetEntity || undefined,
        connectorId: params.connectorId,
        isActive: params.isActive,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      },
    }),

  getById: (id: number) =>
    httpRequest<MappingItem>(`/v1/data-mappings/${id}`, { method: 'GET' }),

  create: (payload: MappingPayload) =>
    httpRequest<{ id: number; name: string; targetEntity: string }>('/v1/data-mappings', {
      method: 'POST',
      body: payload,
    }),

  update: (id: number, payload: MappingPayload) =>
    httpRequest<MappingItem>(`/v1/data-mappings/${id}`, {
      method: 'PUT',
      body: payload,
    }),

  delete: (id: number) =>
    httpRequest<undefined>(`/v1/data-mappings/${id}`, { method: 'DELETE' }),
}

export const sessionApi = {
  list: (params: { mappingId?: number; status?: string; targetEntity?: string; page?: number; pageSize?: number }) =>
    httpRequest<SessionListResponse>('/v1/integration-sessions', {
      method: 'GET',
      query: {
        mappingId: params.mappingId,
        status: params.status || undefined,
        targetEntity: params.targetEntity || undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    }),

  getById: (id: number) =>
    httpRequest<SessionItem>(`/v1/integration-sessions/${id}`, { method: 'GET' }),

  getLogs: (sessionId: number, params?: { action?: string; page?: number; pageSize?: number }) =>
    httpRequest<{ items: LogItem[]; totalCount: number; page: number; pageSize: number }>(
      `/v1/integration-sessions/${sessionId}/logs`,
      {
        method: 'GET',
        query: {
          action: params?.action || undefined,
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 50,
        },
      },
    ),
}
